import { skippedOf, skipReasonLabel, RUN_STATUS_COLOR } from '@/features/purge/purge-run-summary';
import { useT } from '@/i18n';
import { formatDateTime } from '@/utils/datetime';
import type { PurgeRun } from '@restaurant/api-contracts';
import {
  BlockFooter,
  BlockTitle,
  List,
  ListItem,
  NavRight,
  NavTitle,
  Navbar,
  Page,
  Popup,
  Link as F7Link,
} from 'framework7-react';

interface Props {
  run: PurgeRun | null;
  onClose: () => void;
}

export default function PurgeRunDetailPopup({ run, onClose }: Props) {
  const t = useT().purge;
  const h = t.history;

  const summary = run?.criteriaSnapshot ?? null;
  const labels = summary?.labels ?? [];
  const skipped = run ? skippedOf(run) : [];

  return (
    <Popup opened={run !== null} onPopupClosed={onClose}>
      <Page>
        <Navbar>
          <NavTitle>{h.detailTitle}</NavTitle>
          <NavRight>
            <F7Link popupClose>{t.close}</F7Link>
          </NavRight>
        </Navbar>

        {run && summary && (
          <>
            <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
              <ListItem title={h.when} after={formatDateTime(run.startedAt)} />
              <ListItem
                title={h.outcome}
                after={h.statusLabel[run.status]}
                style={{ color: RUN_STATUS_COLOR[run.status] }}
              />
              <ListItem title={h.hiddenCount} after={String(run.affectedCount)} />
              <ListItem title={h.startedBy} after={h.sourceLabel[run.source]} />
            </List>

            <BlockTitle>{h.criteriaTitle}</BlockTitle>
            <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
              <ListItem
                title={h.whatKind}
                after={t[run.target === 'ORDER' ? 'orders' : 'reservations'].title}
              />
              {summary.ruleName && <ListItem title={h.fromRule} after={summary.ruleName} />}
              {summary.olderThanDays !== undefined && (
                <ListItem title={h.olderThan} after={t.olderThanOption(summary.olderThanDays)} />
              )}
              {labels.length > 0 && (
                <ListItem title={h.tags} after={labels.map((l) => l.name).join(', ')} />
              )}
              {summary.ids !== undefined && (
                <ListItem title={h.pickedByHand} after={String(summary.ids.length)} />
              )}
            </List>
            <BlockFooter>{h.criteriaFrozen}</BlockFooter>

            {skipped.length > 0 && (
              <>
                <BlockTitle>{h.skippedTitle}</BlockTitle>
                <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
                  {skipped.map((s) => (
                    <ListItem
                      key={s.reason}
                      title={skipReasonLabel(h.skipReason, s.reason)}
                      after={String(s.count)}
                    />
                  ))}
                </List>
                <BlockFooter>{h.skippedFooter}</BlockFooter>
              </>
            )}

            {run.error && (
              <>
                <BlockTitle>{h.errorTitle}</BlockTitle>
                <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
                  <ListItem title={run.error} />
                </List>
              </>
            )}
          </>
        )}
      </Page>
    </Popup>
  );
}
